import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useLanguage } from '../../contexts/LanguageContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import ImageUploader from '../../components/ImageUploader';
import { resolveImageUrl } from '../../lib/imageUrl';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminPartners() {
  const { t, getLocalizedContent } = useLanguage();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: { fr: '', en: '' },
    logo_url: '',
    website: '',
    order: 0,
    is_active: true
  });

  useEffect(() => { fetchItems(); }, []);

  const fetchItems = async () => {
    try {
      const response = await axios.get(`${API}/partners?active_only=false`);
      setItems(response.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingItem) {
        await axios.put(`${API}/admin/partners/${editingItem.id}`, formData);
        toast.success('Partenaire mis à jour');
      } else {
        await axios.post(`${API}/admin/partners`, formData);
        toast.success('Partenaire créé');
      }
      setShowModal(false);
      resetForm();
      fetchItems();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ?')) return;
    try {
      await axios.delete(`${API}/admin/partners/${id}`);
      toast.success('Supprimé');
      fetchItems();
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      name: item.name || '',
      description: item.description || { fr: '', en: '' },
      logo_url: item.logo_url || '',
      website: item.website || '',
      order: item.order || 0,
      is_active: item.is_active !== false
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setEditingItem(null);
    setFormData({ name: '', description: { fr: '', en: '' }, logo_url: '', website: '', order: 0, is_active: true });
  };

  return (
    <div data-testid="admin-partners">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-[#0F172A] font-['Montserrat']">{t('admin_partners')}</h1>
        <Button onClick={() => { resetForm(); setShowModal(true); }} className="bg-[#0B6AB3] hover:bg-[#095c9e] text-white">
          <Plus className="h-4 w-4 mr-2" />{t('admin_new')}
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0B6AB3]"></div></div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {items.map((item) => (
            <Card key={item.id} className="border-0 shadow-sm text-center">
              <CardContent className="p-4">
                <div className="h-20 flex items-center justify-center mb-4">
                  {item.logo_url ? (
                    <img src={resolveImageUrl(item.logo_url)} alt={item.name} className="max-h-16 max-w-full object-contain" />
                  ) : (
                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center text-gray-400 font-bold text-xl">
                      {item.name?.charAt(0)}
                    </div>
                  )}
                </div>
                <h3 className="font-semibold text-[#0F172A] mb-1">{item.name}</h3>
                <p className="text-sm text-gray-500 mb-4 line-clamp-2">{getLocalizedContent(item.description)}</p>
                <div className="flex items-center justify-center gap-2">
                  <Button variant="ghost" size="sm" onClick={() => handleEdit(item)}><Edit className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="sm" onClick={() => handleDelete(item.id)} className="text-red-500"><Trash2 className="h-4 w-4" /></Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editingItem ? 'Modifier' : 'Nouveau partenaire'}</DialogTitle></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div><Label>Nom *</Label><Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} required /></div>
            <div className="grid md:grid-cols-2 gap-4">
              <div><Label>Description (FR)</Label><Textarea value={formData.description.fr} onChange={(e) => setFormData({ ...formData, description: { ...formData.description, fr: e.target.value } })} rows={2} /></div>
              <div><Label>Description (EN)</Label><Textarea value={formData.description.en} onChange={(e) => setFormData({ ...formData, description: { ...formData.description, en: e.target.value } })} rows={2} /></div>
            </div>
            <div>
              <ImageUploader
                label="Logo du partenaire"
                value={formData.logo_url}
                onChange={(url) => setFormData({ ...formData, logo_url: url })}
                previewSize="sm"
                testIdPrefix="partner-logo"
              />
            </div>
            <div><Label>Site web</Label><Input value={formData.website} onChange={(e) => setFormData({ ...formData, website: e.target.value })} /></div>
            <div><Label>Ordre d'affichage</Label><Input type="number" value={formData.order} onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })} /></div>
            <div className="flex items-center gap-2"><Switch checked={formData.is_active} onCheckedChange={(v) => setFormData({ ...formData, is_active: v })} /><Label>Actif</Label></div>
            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowModal(false)}>{t('common_cancel')}</Button>
              <Button type="submit" className="bg-[#0B6AB3] hover:bg-[#095c9e] text-white">{t('common_save')}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
